/**
 * @fileoverview Content node validation utilities.
 * Provides comprehensive validation for content nodes including text,
 * buttons, quick replies, links, and content naming conventions.
 * @module helperMethods/task_management/content/index
 */

import _ from "lodash";
import store from "@/store";
import moment from "moment";
import { isLinkFound } from "../util";
import { getUserPermissions } from "../index";

import { ButtonType, CardType, ContentNodeType } from "@/store/types";
import { validateButtonOrQuickReply } from "@/components/Buttons/Validations";

/**
 * Validates that a content node has non-empty text content.
 *
 * @param {string} id - Content node ID to validate
 * @returns {boolean} True if content text is invalid/empty
 *
 * @example
 * if (validateContent('node123')) {
 *   // Content is invalid (empty)
 * }
 */
export function validateContent(id: string): boolean {
  const contentNode: ContentNodeType = store.state.nodes.content[id];
  const contentText = _.get(contentNode, "content.text");
  const textIsEmpty = isTextEmpty(contentText);
  const isInvalid = textIsEmpty;
  return isInvalid;
}

/**
 * Checks if content text is empty (handles both string and array formats).
 *
 * @private
 * @param {string | string[]} contentText - Content text to check
 * @returns {boolean} True if text is empty
 */
function isTextEmpty(contentText: string | string[]): boolean {
  if (contentText && typeof contentText !== "string") {
    _.forEach(contentText, (text) => {
      if (!text || text === "") {
        return true;
      }
    });
  } else if (typeof contentText == "string") {
    if (contentText === "") {
      return true;
    }
  }
  return false;
}

/**
 * Validates buttons in a content node (handles single and multiple cards).
 *
 * @param {string} id - Content node ID to validate
 * @returns {boolean} True if any button is invalid
 *
 * @example
 * if (validateButton('node123')) {
 *   // One or more buttons are invalid
 * }
 */
export function validateButton(id: string): boolean {
  const contentNode: ContentNodeType = store.state.nodes.content[id];
  const content = _.get(contentNode, "content");
  const isContentArray = Array.isArray(content);

  let isInvalid = validateSingleCardButton(content as CardType);
  if (isContentArray) {
    isInvalid = validateMultipleCardButtons(content as CardType[]);
  }

  return isInvalid;
}

/**
 * Validates buttons across multiple cards in a carousel.
 *
 * @private
 * @param {CardType[]} contentArray - Array of card content objects
 * @returns {boolean} True if any card has invalid buttons
 */
function validateMultipleCardButtons(contentArray: CardType[]): boolean {
  const isInvalid = _.some(contentArray, (content) => {
    return validateSingleCardButton(content);
  });
  return isInvalid;
}

/**
 * Validates buttons in a single card.
 *
 * @private
 * @param {CardType} content - Single card content object
 * @returns {boolean} True if any button is invalid
 */
function validateSingleCardButton(content: CardType): boolean {
  let isInvalid = false;
  const buttons = _.get(content, "buttons") as ButtonType[];

  if (buttons) {
    isInvalid = _.some(buttons, (button) => {
      return validateButtonOrQuickReply(button);
    });
  }
  return isInvalid;
}

/**
 * Validates quick replies in a content node.
 *
 * @param {string} id - Content node ID to validate
 * @returns {boolean} True if any quick reply is invalid
 *
 * @example
 * if (validateQuickReplies('node123')) {
 *   // One or more quick replies are invalid
 * }
 */
export function validateQuickReplies(id: string): boolean {
  const contentNode: ContentNodeType = store.state.nodes.content[id] || {};
  let isInvalid = false;
  if (contentNode.quickReplies) {
    isInvalid = _.some(contentNode.quickReplies, (reply) => {
      return validateButtonOrQuickReply(reply);
    });
  }
  return isInvalid;
}

/**
 * Validates that the 'next' link in a content node points to a valid target.
 *
 * @param {string} id - Content node ID to validate
 * @returns {boolean} True if the next link is invalid/not found
 *
 * @example
 * if (validateLinks('node123')) {
 *   // The 'next' link points to non-existent content
 * }
 */
export function validateLinks(id: string): boolean {
  const contentNode = store.state.nodes.content[id];
  let isInvalid = false;
  if (!contentNode) {
    return isInvalid;
  }
  if (contentNode.next) {
    const linkNotFound = !isLinkFound(contentNode.next);
    isInvalid = linkNotFound;
  }
  return isInvalid;
}

/**
 * Checks if a content node has a valid 'next' link configured.
 *
 * @param {string} id - Content node ID to check
 * @returns {boolean} True if the node has a valid next link
 *
 * @example
 * if (checkHasLinks('node123')) {
 *   // Node has a valid next link configured
 * }
 */
export function checkHasLinks(id: string): boolean {
  const contentNode = store.state.nodes.content[id];
  if (!contentNode.next) {
    return false;
  }
  const hasLink = isLinkFound(contentNode.next);
  return hasLink;
}

/**
 * Fetches content nodes with missing details from the API if cache has expired.
 * Returns cached data if cache is still valid.
 *
 * @returns {Object} Dictionary of content nodes with missing details, or empty object
 *
 * @example
 * const missingContent = getContentNodeWithMissingDetails();
 * // Returns cached or fetches missing content data if expired
 */
export function getContentNodeWithMissingDetails() {
  // call graphql function
  const missingContentExpiry = _.get(
    store,
    "state.missingContentExpiry",
    moment().subtract(1, "m").format("YYYY-MM-DD HH:mm:ss")
  );
  const currTime = moment();
  if (currTime.isBefore(moment(missingContentExpiry))) {
    store.commit("SET_MISSING_DETAILS", { missingDetailAPI: [] });
    return [];
  }

  const userHasEditorPermissions = getUserPermissions("editor");

  if (userHasEditorPermissions) {
    store
      .dispatch("GET_MISSING_DETAILS")
      .then((missingContent) => {
        if (missingContent) {
          return missingContent;
        }
      })
      .catch((err) => {});
  }
  return {};
}

/**
 * Validates content node naming convention.
 * Content names must be non-empty and contain fewer than 2 colons (for versioning support).
 *
 * @param {string} name - Content node name to validate
 * @returns {boolean} True if name is valid
 *
 * @example
 * isValidContentName('greeting');
 * // Returns: true
 *
 * @example
 * isValidContentName('greeting:v2');
 * // Returns: true
 *
 * @example
 * isValidContentName('greeting:v2:extra');
 * // Returns: false (too many colons)
 */
export function isValidContentName(name: string): boolean {
  const numberOfColons = name?.split(":").length - 1;
  return name !== "" && numberOfColons >= 0 && numberOfColons < 2;
}
